import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/lib/cn';

/**
 * Card de ação em gradiente.
 *
 * Sem `para`, renderiza DESABILITADO em vez de virar um `<Link>` para uma rota
 * que ainda não existe: link que leva ao 404 é pior que um card que se anuncia
 * indisponível. Quando o módulo entrar, é só passar `para`.
 *
 * ⚠️ Card apagado também é o que o usuário SEM PERMISSÃO vê — e aí "Disponível
 * em breve" seria mentira. Por isso `motivoIndisponivel`: quem gate ia o atalho
 * diz por que ele está apagado.
 */
export function CardAtalho({
  icone: Icone,
  imagemIcone,
  titulo,
  descricao,
  para,
  motivoIndisponivel = 'Disponível em breve',
  gradiente = 'bg-gradient-nohra',
}: {
  /** Ícone do lucide. Ignorado quando `imagemIcone` é passado. */
  icone?: LucideIcon;
  /** Caminho de imagem, para quando o ícone é ativo de marca e não um glifo. */
  imagemIcone?: string;
  titulo: string;
  descricao: string;
  para?: string;
  /** Tooltip do estado desabilitado. Só aparece quando não há `para`. */
  motivoIndisponivel?: string;
  gradiente?: string;
}) {
  const conteudo = (
    <>
      {imagemIcone ? (
        // Chip BRANCO, não translúcido: o ícone da Hormonalle é magenta sobre
        // transparente e sumiria contra o gradiente da própria marca.
        <div className="p-1.5 rounded-lg bg-white shadow-sm w-fit">
          <img src={imagemIcone} alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
        </div>
      ) : (
        <div className="p-2.5 rounded-lg bg-white/15 group-hover:bg-white/25 transition-colors w-fit">
          {Icone && <Icone className="w-5 h-5 text-white" />}
        </div>
      )}

      <div className="mt-4">
        <p className="text-sm font-semibold text-white">{titulo}</p>
        <p className="text-xs text-white/80 mt-0.5">{descricao}</p>
      </div>
    </>
  );

  const base = cn(
    gradiente,
    'rounded-lg p-5 border-2 border-brand/20 group flex flex-col justify-between min-h-[8rem]',
  );

  if (!para) {
    return (
      <div
        aria-disabled="true"
        title={motivoIndisponivel}
        className={cn(base, 'opacity-60 cursor-not-allowed')}
      >
        {conteudo}
      </div>
    );
  }

  return (
    <Link to={para} className={cn(base, 'hover:opacity-90 transition-opacity')}>
      {conteudo}
    </Link>
  );
}
